import React, {
  useMemo,
} from "react";

import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CircleDollarSign,
  Euro,
  ExternalLink,
  Users,
} from "lucide-react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  formatSubscriptionDate,
  formatSubscriptionMoney,
  getAssociationName,
} from "../utils/subscriptionFormatters";

function KpiCard({
  label,
  value,
  description,
  icon: Icon,
  variant = "indigo",
}) {
  const variants = {
    indigo:
      "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300",

    emerald:
      "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300",

    orange:
      "bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300",

    violet:
      "bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300",
  };

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
            {label}
          </p>

          <p className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {value}
          </p>
        </div>

        <div
          className={[
            "grid h-11 w-11 shrink-0 place-items-center rounded-2xl",
            variants[
              variant
            ],
          ].join(" ")}
        >
          <Icon
            size={20}
          />
        </div>
      </div>

      <p className="mt-3 text-xs leading-5 text-slate-400">
        {description}
      </p>
    </article>
  );
}

function PriorityItem({
  item,
  onEdit,
}) {
  const subscription =
    item.subscription || {};

  const variants = {
    payment_overdue: {
      bar: "bg-red-500",
      icon: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
    },

    expired: {
      bar: "bg-red-500",
      icon: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
    },

    renewal_to_prepare: {
      bar: "bg-orange-500",
      icon: "bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300",
    },

    renewal_follow_up: {
      bar: "bg-violet-500",
      icon: "bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300",
    },
  };

  const style =
    variants[
      item.type
    ] ||
    variants.renewal_to_prepare;

  return (
    <article className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div
        className={`absolute bottom-0 left-0 top-0 w-1 ${style.bar}`}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${style.icon}`}
        >
          <AlertTriangle
            size={18}
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate font-black text-slate-950 dark:text-white">
            {getAssociationName(
              subscription,
            )}
          </p>

          <p className="mt-0.5 truncate text-sm font-medium text-slate-500 dark:text-slate-400">
            {
              subscription.product
            }
          </p>

          <p className="mt-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
            {item.message}
          </p>
        </div>

        <div className="shrink-0 sm:text-right">
          <p className="text-sm font-black text-slate-950 dark:text-white">
            {formatSubscriptionMoney(
              subscription.total_amount,
            )}
          </p>

          {subscription.end_date && (
            <p className="mt-1 text-xs text-slate-400">
              Échéance{" "}
              {formatSubscriptionDate(
                subscription.end_date,
              )}
            </p>
          )}

          <button
            type="button"
            onClick={() =>
              onEdit?.(
                subscription,
              )
            }
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-black text-indigo-600 hover:text-indigo-800 dark:text-indigo-300"
          >
            Ouvrir

            <ArrowRight
              size={13}
            />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function SubscriptionOverview({
  dashboard,
  isLoading = false,
  onEdit,
  onOpenRenewals,
  onOpenForecast,
}) {
  const summary =
    dashboard?.summary || {};

  const priorities =
    dashboard?.priorities || [];

  const timeline =
    dashboard?.renewalTimeline ||
    [];

  const portfolio =
    dashboard?.productPortfolio ||
    [];

  const forecast =
    dashboard?.forecast || {};

  const priorityPreview =
    priorities.slice(0, 5);

  const topPortfolio =
    portfolio.slice(0, 6);

  const maxClients = useMemo(
    () =>
      Math.max(
        ...topPortfolio.map(
          (item) =>
            Number(
              item.active_clients ||
                0,
            ),
        ),
        1,
      ),
    [topPortfolio],
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map(
            (item) => (
              <div
                key={item}
                className="h-36 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800"
              />
            ),
          )}
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <div className="h-[420px] animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
          <div className="h-[420px] animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI */}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Clients actifs"
          value={
            summary.activeClientsCount ||
            0
          }
          description={`${summary.activeSubscriptionsCount || 0} abonnement(s) actuellement en cours.`}
          icon={Users}
          variant="indigo"
        />

        <KpiCard
          label="Revenu annuel actif"
          value={formatSubscriptionMoney(
            summary.activeAnnualRevenue,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            },
          )}
          description="Valeur actuelle du portefeuille d’abonnements actifs."
          icon={Euro}
          variant="emerald"
        />

        <KpiCard
          label="Devis à préparer"
          value={
            summary.renewalsToPrepareCount ||
            0
          }
          description="Renouvellements arrivés dans la zone d’alerte J-15."
          icon={CalendarClock}
          variant="orange"
        />

        <KpiCard
          label="Prévision 12 mois"
          value={formatSubscriptionMoney(
            summary.forecast12Months,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            },
          )}
          description="Potentiel de renouvellement au tarif actuel."
          icon={CircleDollarSign}
          variant="violet"
        />
      </section>

      {/* PRIORITÉS + TIMELINE */}

      <section className="grid gap-6 2xl:grid-cols-[1.1fr_0.9fr]">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5 dark:border-slate-800 sm:p-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">
                Priorités
              </p>

              <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white">
                À traiter maintenant
              </h2>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Seulement les
                dossiers nécessitant
                réellement une action.
              </p>
            </div>

            <div className="rounded-2xl bg-red-50 px-3 py-2 text-center dark:bg-red-950/30">
              <p className="text-xl font-black text-red-600 dark:text-red-300">
                {
                  summary.urgentActionsCount ||
                  0
                }
              </p>

              <p className="text-[10px] font-bold uppercase tracking-wide text-red-500">
                actions
              </p>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {priorityPreview.length ===
            0 ? (
              <div className="grid min-h-[260px] place-items-center rounded-2xl border border-dashed border-slate-200 text-center dark:border-slate-700">
                <div>
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
                    ✓
                  </div>

                  <p className="mt-3 font-bold text-slate-700 dark:text-slate-200">
                    Rien d’urgent
                  </p>

                  <p className="mt-1 text-sm text-slate-400">
                    Aucun dossier ne
                    nécessite
                    d’intervention.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {priorityPreview.map(
                  (
                    item,
                    index,
                  ) => (
                    <PriorityItem
                      key={`${item.type}-${item.subscription?.id}-${index}`}
                      item={
                        item
                      }
                      onEdit={
                        onEdit
                      }
                    />
                  ),
                )}
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-start justify-between border-b border-slate-100 p-5 dark:border-slate-800 sm:p-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">
                À venir
              </p>

              <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white">
                Prochains
                renouvellements
              </h2>
            </div>

            <button
              type="button"
              onClick={
                onOpenRenewals
              }
              className="inline-flex items-center gap-1 text-sm font-black text-indigo-600 hover:text-indigo-800 dark:text-indigo-300"
            >
              Pipeline

              <ArrowRight
                size={15}
              />
            </button>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid gap-3 sm:grid-cols-2">
              {timeline.map(
                (month) => (
                  <article
                    key={
                      month.key
                    }
                    className="rounded-2xl border border-slate-200 p-4 dark:border-slate-800"
                  >
                    <p className="text-xs font-black uppercase tracking-wider text-slate-400">
                      {
                        month.label
                      }
                    </p>

                    <div className="mt-3 flex items-end justify-between gap-3">
                      <div>
                        <p className="text-2xl font-black text-slate-950 dark:text-white">
                          {
                            month.count
                          }
                        </p>

                        <p className="text-xs text-slate-400">
                          renouvellement
                          {month.count >
                          1
                            ? "s"
                            : ""}
                        </p>
                      </div>

                      <p className="text-sm font-black text-indigo-600 dark:text-indigo-300">
                        {formatSubscriptionMoney(
                          month.amount,
                          {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          },
                        )}
                      </p>
                    </div>

                    {month.items
                      ?.slice(
                        0,
                        2,
                      )
                      .map(
                        (
                          subscription,
                        ) => (
                          <button
                            key={
                              subscription.id
                            }
                            type="button"
                            onClick={() =>
                              onEdit?.(
                                subscription,
                              )
                            }
                            className="mt-3 block w-full truncate text-left text-xs font-semibold text-slate-600 hover:text-indigo-600 dark:text-slate-300 dark:hover:text-indigo-300"
                          >
                            •{" "}
                            {getAssociationName(
                              subscription,
                            )}
                          </button>
                        ),
                      )}
                  </article>
                ),
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUITS + FORECAST */}

      <section className="grid gap-6 2xl:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500">
            Parc actif
          </p>

          <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white">
            Clients par produit
          </h2>

          <div className="mt-6 space-y-5">
            {topPortfolio.map(
              (item) => {
                const percentage =
                  Math.max(
                    (Number(
                      item.active_clients ||
                        0,
                    ) /
                      maxClients) *
                      100,
                    4,
                  );

                return (
                  <div
                    key={
                      item.product_family
                    }
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-slate-800 dark:text-slate-200">
                          {
                            item.product_family
                          }
                        </p>

                        <p className="mt-0.5 text-xs text-slate-400">
                          {formatSubscriptionMoney(
                            item.annual_revenue,
                            {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            },
                          )}{" "}
                          / an
                        </p>
                      </div>

                      <p className="shrink-0 text-xl font-black text-slate-950 dark:text-white">
                        {
                          item.active_clients
                        }
                      </p>
                    </div>

                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full bg-indigo-500"
                        style={{
                          width: `${percentage}%`,
                        }}
                      />
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-start justify-between gap-3 p-5 sm:p-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-500">
                Projection
              </p>

              <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white">
                Renouvellements
                sur 12 mois
              </h2>
            </div>

            <button
              type="button"
              onClick={
                onOpenForecast
              }
              className="inline-flex items-center gap-1 text-sm font-black text-indigo-600 hover:text-indigo-800 dark:text-indigo-300"
            >
              Détail

              <ExternalLink
                size={14}
              />
            </button>
          </div>

          <div className="h-[320px] px-2 pb-5">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={
                  forecast.months ||
                  []
                }
                margin={{
                  top: 10,
                  right: 20,
                  left: 10,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  vertical={
                    false
                  }
                  strokeDasharray="4 4"
                  strokeOpacity={
                    0.35
                  }
                />

                <XAxis
                  dataKey="label"
                  axisLine={
                    false
                  }
                  tickLine={
                    false
                  }
                  tick={{
                    fontSize: 11,
                    fill: "#64748b",
                  }}
                />

                <YAxis
                  axisLine={
                    false
                  }
                  tickLine={
                    false
                  }
                  width={65}
                  tick={{
                    fontSize: 11,
                    fill: "#64748b",
                  }}
                  tickFormatter={(
                    value,
                  ) =>
                    `${Math.round(
                      Number(
                        value ||
                          0,
                      ),
                    )} €`
                  }
                />

                <Tooltip
                  formatter={(
                    value,
                  ) =>
                    formatSubscriptionMoney(
                      value,
                    )
                  }
                />

                <Bar
                  dataKey="potential_revenue"
                  name="Renouvellements potentiels"
                  fill="#7c3aed"
                  radius={[
                    7,
                    7,
                    0,
                    0,
                  ]}
                  maxBarSize={
                    38
                  }
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
}